import React, { useState, useEffect, useMemo } from "react";
import "./Dashboard.css";
import { useNavigate, useParams } from "react-router-dom";
import { auth, signOut, onAuthStateChanged } from "../firebase";
import useCircleLayout from "../hooks/useCircleLayout";

const baseUrl = process.env.REACT_APP_API_URL;

export default function Dashboard() {
    const [user, setUser] = useState(null);
    const [isOwner, setIsOwner] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const { username } = useParams();
    const navigate = useNavigate();
    const [userEmail, setUserEmail] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [currentCircle, setCurrentCircle] = useState(0);
    const [hoverTooltip, setHoverTooltip] = useState(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setIsLoggedIn(true);
                setUserEmail(user.email);
                const res = await fetch(`${baseUrl}/api/users/email/${encodeURIComponent(user.email)}`);
                const data = await res.json();
                setCurrentUser(data);
            } else {
                setIsLoggedIn(false);
                setUserEmail(null);
                setCurrentUser(null);
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!username) return;
        const fetchUserData = async () => {
            try {
                const res = await fetch(`${baseUrl}/api/users/@${username}`);
                const data = await res.json();
                setUser(data);
                setIsOwner(userEmail && data.email === userEmail);
            } catch (err) {
                console.error("Fetch Error:", err);
            }
        };
        fetchUserData();
    }, [username, userEmail]);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            localStorage.removeItem("username");
            setUser(null);
            navigate("/");
        } catch (err) {
            console.error("Logout failed:", err);
        }
    };

    const handleJoinCircle = async (ownerId) => {
        try {
            const res = await fetch(`${baseUrl}/api/users/circle/request`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ fromUserId: currentUser._id, toUserId: ownerId })
            });
            const data = await res.json();
            alert(data.message || "Request sent!");
        } catch (err) {
            console.error("Join Circle failed:", err);
        }
    };

    const handlePrev = () => setCurrentCircle(Math.max(0, currentCircle - 1));
    const handleNext = () => setCurrentCircle(prev => prev + 1 < user.circles.length ? prev + 1 : prev);

    const currentCircleData = user?.circles?.[currentCircle];
    const previewUsers = useMemo(() => {
        const current = user?.circles?.[currentCircle];
        if (!current || !user?.circleMembers) return [];
        return current.order
            .map(id => user.circleMembers.find(m => m._id === id))
            .filter(Boolean)
            .slice(0, 5);
    }, [user, currentCircle]);


    const { containerRef, avatarStyles } = useCircleLayout(previewUsers);

    return (
        <div className="dashboard-page">
            <div className="dashboard-container">
                {!isOwner && (
                    <button className="dashboard-exit-button" onClick={() => navigate(-1)}
                    >
                        ✕
                    </button>
                )}

                {user && (
                    <>
                        <div className="dashboard-header-grid">
                            <div className="profile-grid-left">
                                <div className="profile-top">
                                    <img
                                        src={user.profileImage?.trim() ? `${baseUrl}${user.profileImage}` : `${baseUrl}/uploads/default.png`}
                                        alt="Profile"
                                        className="profile-image"
                                    />
                                    <div className="profile-text">
                                        <h1 className="profile-name">{user.name}</h1>
                                        <h2 className="profile-headline">{user.headline || "\u00A0"}</h2>
                                        <p className="profile-bio">{user.bio || "\u00A0"}</p>

                                        {user.socials && Object.values(user.socials).some(Boolean) && (
                                            <div className="social-icons">
                                                {Object.entries(user.socials).map(([key, value]) => (
                                                    value ? <a key={key} href={value} target="_blank" rel="noreferrer"><img src={`/socials/${key}.png`} alt={key} /></a> : null
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <section className="links-section">
                                    {user.links?.length > 0 && (
                                        <div className="links-list">
                                            {user.links.map((link) => (
                                                <div key={link._id} className="link-card" onClick={() => window.open(link.url, "_blank")}>
                                                    <span style={{ flex: 1, textAlign: "center" }}>{link.title}</span>
                                                    <div style={{ fontSize: "3rem", color: "#555" }}>&#x22EE;</div>

                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </section>

                                {isLoggedIn && currentUser?.username && (
                                    <div style={{ fontSize: "0.9rem", color: "#666", marginTop: "auto" }}>
                                        Logged in as: <strong>{currentUser.username}</strong>
                                    </div>
                                )}
                            </div>

                            <div className="profile-grid-right">
                                <div className="top-right-row">
                                    <div style={{ display: "flex", alignItems: "center", gap: "16px", transform: "translateY(-20px)" }}>
                                        <button className="icon-button" onClick={() => navigate("/search")} aria-label="Search">
                                            <img src="/icons/search.png" alt="Search Icon" style={{ width: "24px", height: "24px" }} />
                                        </button>

                                        {isLoggedIn && (
                                            <button className="icon-button" onClick={() => navigate("/user/notifications")} aria-label="Notifications">
                                                <img src="/icons/bell.png" alt="Bell Icon" style={{ width: "20px", height: "20px" }} />
                                            </button>
                                        )}
                                    </div>

                                    <div className="button-group">
                                        {isLoggedIn && !isOwner && currentUser?.username && (
                                            <button
                                                className="dashboard-button"
                                                onClick={() => navigate(`/${currentUser.username.replace(/^@/, "")}`)}
                                            >
                                                My Profile
                                            </button>
                                        )}

                                        {isOwner && (
                                            <button
                                                className="dashboard-button"
                                                onClick={() => navigate(`/userprofile/${username.replace(/^@/, "")}`)}
                                            >
                                                Edit Profile
                                            </button>
                                        )}

                                        {isLoggedIn ? (
                                            <button className="dashboard-button" onClick={handleLogout}>Log Out</button>
                                        ) : (
                                            <button className="dashboard-button" onClick={() => navigate("/login")}>Login</button>
                                        )}
                                    </div>

                                    <img src="/logo.png" alt="Logo" className="dashboard-logo" />
                                </div>


                                <section className="circle-section">
                                    {currentCircleData && (
                                        <div className="circle-card">
                                            <div className="circle-header-row">
                                                <button className="circle-arrow" onClick={handlePrev} disabled={currentCircle === 0}>‹</button>
                                                <h3 className="circle-title">{user.circles[currentCircle].name}</h3>
                                                <button className="circle-arrow" onClick={handleNext} disabled={currentCircle >= user.circles.length - 1}>›</button>
                                            </div>
                                            <button
                                                className="circle-action-button"
                                                onClick={() => {
                                                    if (isOwner) {
                                                        navigate(`/userprofile/${username.replace(/^@/, "")}?tab=circles`);
                                                    } else if (isLoggedIn) {
                                                        currentCircleData.members?.includes(currentUser?._id)
                                                            ? navigate(`/circle/${username.replace(/^@/, "")}/${currentCircle}`)
                                                            : handleJoinCircle(user._id);
                                                    } else {
                                                        navigate("/login");
                                                    }
                                                }}
                                            >
                                                {isOwner
                                                    ? "Edit Circle"
                                                    : isLoggedIn
                                                        ? (currentCircleData.members?.includes(currentUser?._id)
                                                            ? "View All"
                                                            : "Join Circle")
                                                        : "Join Circle"}
                                            </button>

                                            <div className="circle-avatars" ref={containerRef}>
                                                {previewUsers.map((member, i) => (
                                                    <div
                                                        key={member._id}
                                                        className="circle-avatar"
                                                        style={avatarStyles[i]}
                                                        onClick={() => {
                                                            setHoverTooltip(null);
                                                            navigate(`/${member.username.replace(/^@/, "")}`)
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                            setHoverTooltip({
                                                                name: member.name,
                                                                headline: member.headline,
                                                                x: rect.left + rect.width / 2,
                                                                y: rect.top,
                                                            });
                                                        }}
                                                        onMouseLeave={() => setHoverTooltip(null)}
                                                    >
                                                        <div className="avatar-image-wrapper">
                                                            <img
                                                                src={member.profileImage?.trim() ? `${baseUrl}${member.profileImage}` : `${baseUrl}/uploads/default.png`}
                                                                alt={member.name}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}


                                            </div>

                                        </div>
                                    )}
                                    <div className="circle-indicators">
                                        {user.circles?.map((_, index) => (
                                            <div
                                                key={index}
                                                className={`circle-indicator ${index === currentCircle ? 'active' : ''}`}
                                                onClick={() => setCurrentCircle(index)}
                                            />
                                        ))}
                                    </div>

                                </section>

                            </div>
                        </div>
                    </>
                )}
                {hoverTooltip && (
                    <div
                        style={{
                            position: "fixed",
                            top: hoverTooltip.y,
                            left: hoverTooltip.x,
                            transform: "translate(0%, -90%)",
                            backgroundColor: "rgba(0,0,0,0.85)",
                            color: "#fff",
                            padding: "6px 10px",
                            borderRadius: "8px",
                            whiteSpace: "nowrap",
                            fontSize: "1.2rem",
                            zIndex: 9999,
                            pointerEvents: "none",
                        }}

                    >
                        <div style={{ fontWeight: "bold" }}>{hoverTooltip.name}</div>
                        <div style={{ fontSize: "1rem", color: "#ccc" }}>{hoverTooltip.headline}</div>
                    </div>
                )}

            </div>
        </div>
    );
}
